import React from 'react';
import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgCircleExclamationFilled from '@icons/circle-exclamation-filled.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { groupSummaryRows, SummaryRow } from '@components/RingilComponents/ValidationSummary/validationSummary.ts';

interface ComponentProps {
  /** Already-resolved rows; build them with `buildValidationRows` and whatever else the form knows. */
  rows: SummaryRow[];
  heading?: string;
  description?: string;
  dataTestId?: string;
}

/**
 * Everything standing between the form and being saved, in one list at the top of the page.
 *
 * A toast says only that something is wrong, and the red fields saying what are spread down a long
 * page — some of them inside a section that is collapsed, or behind a tag that has to be opened, or
 * with no field on screen at all. The list is the one place that is guaranteed to name them all.
 *
 * Grouped per part of the form rather than shown as one long run of bullets: a dozen errors are
 * almost always a handful of places needing attention, and the grouping is what makes that visible.
 *
 * Not dismissible and not a link anywhere: it is a statement of what is left to do, and it goes away
 * when the form is submitted again with those things fixed.
 */
const ValidationSummaryPanel: React.FC<ComponentProps> = ({ rows, heading, description, dataTestId = 'validationSummary' }) => {
  const { tr } = useTranslationLgs();

  const groups = groupSummaryRows(rows ?? []);
  if (groups.length === 0) return null;

  const total = groups.reduce((count, group) => count + group.rows.length, 0);

  return (
    <Card>
      <div className={'flex w-full flex-col gap-4'} data-testid={dataTestId}>
        <COHeadline
          variant={'h4'}
          icon={<SvgCircleExclamationFilled />}
          iconColor={'var(--complementary-error)'}
          title={heading ?? tr('ValidationSummary.heading', '{{count}} things need fixing before this can be saved', { count: total }, total)}
        />

        <span className={'text-content-secondary'}>
          {description ??
            tr('ValidationSummary.description', 'Nothing has been saved yet. Sort these out and try again — they are listed in the order they appear on the page.')}
        </span>

        <div className={'flex flex-col gap-2'}>
          {groups.map(group => (
            <div key={group.group} className={'overflow-hidden rounded border border-solid border-content-quaternary'} data-testid={`${dataTestId}-group`}>
              <div className={'flex flex-row items-center justify-between gap-4 bg-base-tertiary px-3 py-2'}>
                <span className={'font-medium text-content-primary'}>{group.group}</span>
                <span className={'rounded-full bg-complementary-errorMedium px-2 py-[2px] text-xxs font-medium text-content-primary'}>{group.rows.length}</span>
              </div>

              <ul className={'m-0 flex list-none flex-col p-0'}>
                {group.rows.map(row => (
                  <li
                    key={`${group.group}-${row.key}`}
                    className={'border-0 border-t border-solid border-content-quaternary px-3 py-2'}
                    data-testid={`${dataTestId}-row`}
                  >
                    {row.label && <span className={'font-medium text-content-primary'}>{row.label} — </span>}
                    <span className={'text-content-secondary'}>{row.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default ValidationSummaryPanel;
